const enum Feature {
  API_ACCESS = 'API_ACCESS',
  AGENT_GARDEN_BYOM = 'AGENT_GARDEN_BYOM',
  BETA_API_EXPLORER = 'BETA_API_EXPLORER',
  EVENT_STREAMS = 'EVENT_STREAMS',
  SUPPORT_URL = 'SUPPORT_URL',
  BUNDLE_UI = 'BUNDLE_UI',
  INTEGRATION_PACKS = 'INTEGRATION_PACKS',
  ACCT_GRP = 'ACCT_GRP',
  CUSTOM_CATALOGS = 'CUSTOM_CATALOGS',
  PROCESS_LIBRARY = 'PROCESS_LIBRARY',
  AI_UI = 'AI_UI',
  AGENT_GARDEN_UI = 'AGENT_GARDEN_UI',
  AI_TNC = 'AI_TNC', // need to change this variable into AI_TNC
  THEME_SELECTION = 'THEME_SELECTION',
  SOURCES_MANAGEMENT = 'SOURCES_MANAGEMENT',
  KNOWLEDGE_HUB = 'KNOWLEDGE_HUB',
  META_HUB = 'METAHUB',
}

const enum FeatureVisibility {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  INVISIBLE = 'INVISIBLE',
}

/**
 * General Features utility functions
 */
const Features = {
  /**
   * Is Login User white Label Enabled
   */
  isWhiteLabelEnabled(user: any): boolean {
    return !!user?.Account?.Org;
  },

  /**
   * Get Custom Branding details
   */
  getCustomBranding(user: any): any {
    return user?.Account?.Org?.CustomBranding ?? null;
  },

  /**
   * Get the feature object for given feature
   */
  getFeature(user: any, feature: string): any {
    // first check in user.Features
    let featureObj = null;
    if (user?.Feature) {
      featureObj = user?.Feature.find((element:any) => element.key === feature) || null;
    }
    // check in user.Account.features
    if (featureObj === null) {
      if (user?.Account?.feature) {
        featureObj = user.Account.feature.find((element:any) => element.key === feature) || null;
      }
    }

    return featureObj;
  },

  /**
   * Is given feature enabled
   */
  isFeatureEnabled(user: any, feature: string): boolean {
    const featureObj = this.getFeature(user, feature);
    if (featureObj !== null && featureObj.value === FeatureVisibility.ENABLED) {
      return true;
    }

    return false;
  },

  /**
   * Get extended property from feature
   */
  getFeatureExtendedProperty(user: any, feature: string): any {
    let property = null;
    const featureObj = this.getFeature(user, feature);

    if (featureObj != null && featureObj.extendedProperty != null) {
      property = featureObj.extendedProperty;
    }

    return property;
  },

  isApiExplorerEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.BETA_API_EXPLORER);
  },

  canUseApiExplorer(user: any) {
    return this.isFeatureEnabled(user, Feature.API_ACCESS) && this.isApiExplorerEnabled(user);
  },

  isEventStreamsFeatureEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.EVENT_STREAMS);
  },

  getEventStreamsExtendedProperty(user: any) {
    return this.getFeatureExtendedProperty(user, Feature.EVENT_STREAMS);
  },

  getSupportURLFromFeature(user: any) {
    return this.getFeatureExtendedProperty(user, Feature.SUPPORT_URL);
  },

  isBundleUIFeatureEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.BUNDLE_UI);
  },

  isIntegrationPacksFeatureEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.INTEGRATION_PACKS);
  },

  isAcctGrpFeatureEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.ACCT_GRP);
  },

  isCustomCatalogsFeatureEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.CUSTOM_CATALOGS);
  },

  isProcessLibraryFeatureEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.PROCESS_LIBRARY);
  },

  isAiFlagEnabled(user: any) {
    const featureObj = this.getFeature(user, Feature.AI_UI);
    if (featureObj !== null && featureObj.value === FeatureVisibility.DISABLED) {
      return false;
    }

    return true;
  },

  isNewMastheadEnabled(userResponse: any): boolean {
    const newMastheadFeature = userResponse?.user?.Feature?.find(
      (feature: { key: string }) => feature?.key === 'MASTHEAD_VERSION_TWO',
    );
    return newMastheadFeature ? newMastheadFeature.value === 'ENABLED' : false;
  },

  isAgentGardenFlagEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.AGENT_GARDEN_UI);
  },

  isTnCFlagEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.AI_TNC);
  },

  isSourcesManagementEnabled() {
    return true;
  },
  isDarkModeEnabled(user: any) {
    return this.getFeatureExtendedProperty(user, Feature.THEME_SELECTION) === 'DARK';
  },

  isKnowledgeHubEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.KNOWLEDGE_HUB);
  },

  isMetaHubEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.META_HUB);
  },
  isAgentGardenByomEnabled(user: any) {
    return this.isFeatureEnabled(user, Feature.AGENT_GARDEN_BYOM);
  },
};

export default Features;
